import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { Tab, TabList, Tabs } from '@carbon/react';
import { Box, Finance, Money, User, Warning } from '@carbon/react/icons';
import { useTenantContext } from 'src/context/useTenantContext';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { useDashboardStore } from 'src/states/dashboard';
import DateRangeMenu from './DateRangeMenu';

interface ToolbarProps {}

const routeLabels = [
  { label: 'selling', icon: Money },
  { label: 'accounting', icon: Finance },
  { label: 'inventory', icon: Box },
  { label: 'staff', icon: User },
  { label: 'issues', icon: Warning },
];

const getActiveTabIndex = (routes, pathname) => {
  const idx = routes.findIndex((route) => pathname === route.url || pathname === `${route.url}/`);
  return idx === -1 ? 0 : idx;
};

const Toolbar = React.memo((_: ToolbarProps) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const { buildPath } = useTenantContext();
  const { reportingTimezone } = useCurrentRestaurant() ?? {};
  const timezone = reportingTimezone || 'Asia/Ho_Chi_Minh';
  const routes = useMemo(
    () =>
      routeLabels.map((route) => ({
        label: route.label,
        url: buildPath(`dashboard/${route.label}`),
        icon: route.icon,
      })),
    [buildPath]
  );
  const activeTabIndex = useMemo(() => getActiveTabIndex(routes, location.pathname), [location.pathname, routes]);
  const { startDate, endDate, dateRange, setDates } = useDashboardStore(
    (state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      dateRange: state.dateRange,
      setDates: state.setDates,
    }),
    shallow
  );

  return (
    <div className="toolbar">
      <Tabs selectedIndex={activeTabIndex}>
        <TabList className="tabs-group" contained>
          {routes.map((route) => (
            <Tab
              key={route.url}
              renderIcon={route.icon}
              onClick={() => navigate(route.url, { state: { previousPath: location.pathname } })}
            >
              {t(route.label)}
            </Tab>
          ))}
        </TabList>
      </Tabs>
      <div className={'toolbar__dates'}>
        <DateRangeMenu
          startDate={startDate}
          endDate={endDate}
          dateRange={dateRange}
          timezone={timezone}
          onChange={setDates}
        />
      </div>
    </div>
  );
});

export default Toolbar;
